import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@1trading/react';
import { CircleAlert } from 'lucide-react';
import { Fragment } from 'react';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { StatusChip } from '@/components/ui/status-chip';
import { useBrokerFeeSettlements } from '@/hooks/queries/brokers/use-broker-fee-settlements';
import type { WireFeeSettlement } from '@/services/api/broker-fee-settlements';
import { formatDateOnly } from '@/utils/format';
import { formatSnakeCase } from '@/utils/string';
import { CHIP_XS_CLASS, feeSettlementStatusVariant } from './broker-detail-format';
import { ErrorPanel, LoadMoreButton } from './detail-panels';

const COLUMN_COUNT = 7;

/** "17 Aug 2026 → 24 Aug 2026" — period_to is exclusive (Monday-to-Monday week). */
const formatPeriod = (settlement: WireFeeSettlement) => `${formatDateOnly(settlement.period_from)} → ${formatDateOnly(settlement.period_to)}`;

/** Amounts are decimal strings — render verbatim, never Number() them. */
const withCurrency = (amount: string | null | undefined, currency: string) => (amount ? `${amount} ${currency}` : '—');

interface FeeSettlementsTabProps {
  brokerId: string;
}

/**
 * Read-only — settlement review/approval (pay or reject) happens through the
 * broker-alert flow (alert-actions/broker-fee-settlement-review.tsx), not on this page.
 * Rejection/failure reason rows are ALWAYS visible, not toggled, so this uses the
 * raw Table primitives instead of DataTable (same rationale as fee-bands-tab).
 */
export const FeeSettlementsTab = ({ brokerId }: FeeSettlementsTabProps) => {
  const query = useBrokerFeeSettlements(brokerId);

  if (query.isPending) return <LoadingPanel />;
  // Full panel only when nothing loaded; a failed Load More keeps the rows on
  // screen with the error inline below (isError with retained data).
  if (query.isError && !query.data) return <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch fee settlements" />;

  const settlements = query.data?.items ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md border">
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Total Fees</TableHead>
              <TableHead>Broker Payment</TableHead>
              <TableHead>Trades</TableHead>
              <TableHead>Liquidations</TableHead>
              <TableHead>Payment Reference</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {settlements.length === 0 ? (
              <TableRow>
                <TableCell className="h-24 text-center text-muted-foreground" colSpan={COLUMN_COUNT}>
                  No fee settlements found.
                </TableCell>
              </TableRow>
            ) : (
              settlements.map((settlement) => (
                <Fragment key={settlement.settlement_id}>
                  <TableRow>
                    <TableCell className="whitespace-nowrap tabular-nums">{formatPeriod(settlement)}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">{withCurrency(settlement.total_fees, settlement.currency)}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      <div className="flex flex-col">
                        <span className="font-medium">{withCurrency(settlement.payable_amount, settlement.currency)}</span>
                        <span className="text-xs text-muted-foreground">{settlement.broker_payment} exact</span>
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums">{settlement.trade_count}</TableCell>
                    <TableCell className="tabular-nums">{settlement.liquidation_count}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs">{settlement.payment_reference}</TableCell>
                    <TableCell>
                      <StatusChip className={CHIP_XS_CLASS} variant={feeSettlementStatusVariant(settlement.status)}>
                        {formatSnakeCase(settlement.status)}
                      </StatusChip>
                    </TableCell>
                  </TableRow>
                  {settlement.rejection_reason ? (
                    <TableRow className="bg-red-500/10 hover:bg-red-500/10">
                      <TableCell colSpan={COLUMN_COUNT}>
                        <span className="flex items-center gap-2 text-sm text-red-500">
                          <CircleAlert aria-hidden className="size-4 shrink-0" />
                          Rejection reason: {settlement.rejection_reason}
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {settlement.failure_reason ? (
                    <TableRow className="bg-red-500/10 hover:bg-red-500/10">
                      <TableCell colSpan={COLUMN_COUNT}>
                        <span className="flex items-center gap-2 text-sm text-red-500">
                          <CircleAlert aria-hidden className="size-4 shrink-0" />
                          Failure reason: {settlement.failure_reason}
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {query.isError ? (
        <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch fee settlements" />
      ) : (
        <LoadMoreButton hasNextPage={query.hasNextPage} isFetchingNextPage={query.isFetchingNextPage} onLoadMore={() => query.fetchNextPage()} />
      )}
    </div>
  );
};
FeeSettlementsTab.displayName = 'FeeSettlementsTab';
